import { ReactNode, useCallback, useState } from 'react';
import { Box, ButtonBase, SxProps, Theme, Typography } from '@mui/material';
import { AuthentificationFlow } from '@/components/blocks/AuthentificationFlow';
import { CouponTicketIcon } from '@/components/blocks/DpRepurchaseCoupon/parts/CouponIcons';
import {
	repurchaseCheckboxSX,
	repurchaseCouponAvailableHeadSX,
	repurchaseCouponTextColumnSX,
} from '@/components/blocks/DpRepurchaseCoupon/styles/container';
import {
	repurchaseCouponTextSX,
	repurchaseStrongSX,
	repurchaseUrgencyInlineSX,
	repurchaseUrgencyLineSX,
	repurchaseUrgencySX,
} from '@/components/blocks/DpRepurchaseCoupon/styles/typography';
import { RegistrationOrigin } from '@/data/Content/Registration';
import {
	cancelActivationOnLogin,
	RepurchaseActivationSource,
	requestActivationOnLogin,
	setActivationSource,
} from '@/data/Content/RepurchaseCouponState';
import { useUser } from '@/data/User';
import {
	COUPON_URGENCY_LINE,
	COUPON_URGENCY_SUFFIX,
	getCouponUrgency,
} from '@/components/blocks/DpRepurchaseCoupon/formatCouponDate';

export const useRepurchaseActivation = (
	onApply: () => void,
	source: RepurchaseActivationSource
) => {
	const { user } = useUser();
	const isLoggedIn = !!user?.isLoggedIn;
	const [loginOpen, setLoginOpen] = useState(false);

	const onActivate = useCallback(() => {
		setActivationSource(source);
		if (isLoggedIn) {
			onApply();
			return;
		}
		requestActivationOnLogin(source);
		setLoginOpen(true);
	}, [isLoggedIn, onApply, source]);

	const onLoginClose = useCallback(() => {
		cancelActivationOnLogin();
		setLoginOpen(false);
	}, []);

	const onLoginSuccess = useCallback(() => setLoginOpen(false), []);

	return { isLoggedIn, loginOpen, onActivate, onLoginClose, onLoginSuccess };
};

export const RepurchaseLoginFlow = ({
	open,
	origin = 'unknown',
	onClose,
	onSuccess,
}: {
	open: boolean;
	origin?: RegistrationOrigin;
	onClose: () => void;
	onSuccess: () => void;
}) => (
	<AuthentificationFlow
		open={open}
		type="login"
		origin={origin}
		onClose={onClose}
		onAuthSuccess={onSuccess}
	/>
);

const getActivateLabel = (isLoggedIn: boolean, expiring: boolean) => {
	if (!isLoggedIn) {
		return { before: '', after: ', Inicia sesión para activarlo' };
	}
	return { before: expiring ? 'Activa tu ' : 'Activa tu cupón ', after: '' };
};

type Props = {
	percentage: number;
	expirationDateTime?: string;
	onApply: () => void;
	source: RepurchaseActivationSource;
	sx: SxProps<Theme>;
	origin?: RegistrationOrigin;
	urgencyBelow?: boolean;
	children?: ReactNode;
};

export const RepurchaseActivateCheck = ({
	percentage,
	expirationDateTime,
	onApply,
	source,
	sx,
	origin,
	urgencyBelow = false,
	children,
}: Props) => {
	const { isLoggedIn, loginOpen, onActivate, onLoginClose, onLoginSuccess } =
		useRepurchaseActivation(onApply, source);
	const urgency = getCouponUrgency(expirationDateTime);
	const label = getActivateLabel(isLoggedIn, !!urgency);

	return (
		<>
			<ButtonBase role="checkbox" aria-checked={false} onClick={onActivate} sx={sx}>
				<Box component="span" sx={repurchaseCouponAvailableHeadSX}>
					<Box component="span" aria-hidden="true" sx={repurchaseCheckboxSX} />
					<CouponTicketIcon />
					<Box component="span" sx={repurchaseCouponTextColumnSX}>
						<Typography component="span" sx={repurchaseCouponTextSX}>
							{label.before}
							<Typography component="strong" sx={repurchaseStrongSX}>
								{`${percentage}% OFF`}
							</Typography>
							{label.after}
							{urgency ? (
								<Box
									component="span"
									sx={urgencyBelow ? repurchaseUrgencyInlineSX : repurchaseUrgencySX}
								>
									{COUPON_URGENCY_SUFFIX[urgency]}
								</Box>
							) : null}
							{urgency && urgencyBelow ? (
								<Box component="span" sx={repurchaseUrgencyLineSX}>
									{COUPON_URGENCY_LINE[urgency]}
								</Box>
							) : null}
						</Typography>
						{children}
					</Box>
				</Box>
			</ButtonBase>
			<RepurchaseLoginFlow
				open={loginOpen}
				origin={origin}
				onClose={onLoginClose}
				onSuccess={onLoginSuccess}
			/>
		</>
	);
};
